import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import authService from '../services/authService';
import { addBreadcrumb, logger, setTag, setContext } from '../utils/sentryUtils';

const HouseholdProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [authData, setAuthData] = useState(null);

  useEffect(() => {
    // Check if user is authenticated using JWT tokens
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const user = authService.getUser();
          if (user) {
            // Initialize Sentry user context
            authService.initializeSentryContext();
            
            // Extract household ID from user data
            let householdId = 1; // Default fallback
            if (user.householdIds && user.householdIds.length > 0) {
              // Use the first household ID from the array
              householdId = user.householdIds[0];
            }
            
            const authDataObj = {
              householdId: householdId,
              userId: user.id,
              userEmail: user.email,
              userName: user.preferredName || user.firstName || user.email
            };
            
            setAuthData(authDataObj);
            setIsAuthenticated(true);
            
            // Set Sentry context and tags for household access
            setContext("household", {
              householdId: householdId,
              userId: user.id,
              userEmail: user.email,
              userName: authDataObj.userName
            });
            
            setTag("user.household_id", householdId.toString());
            setTag("user.access_type", "household");
            
            addBreadcrumb("Household route accessed", "navigation", { 
              householdId: householdId,
              userId: user.id,
              route: window.location.pathname
            });
            
            logger.info("User accessing household route", { 
              householdId: householdId,
              userId: user.id,
              userEmail: user.email,
              route: window.location.pathname
            });
          }
        }
      } catch (error) {
        logger.error("Error checking household authentication", { 
          error: error.message,
          route: window.location.pathname
        });
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleLogout = () => {
    addBreadcrumb("User logging out from household route", "auth", { 
      householdId: authData?.householdId,
      userId: authData?.userId
    });
    
    logger.info("User logging out from household route", { 
      householdId: authData?.householdId,
      userId: authData?.userId
    });
    
    // Use the JWT-based logout from authService
    authService.logout();
    setIsAuthenticated(false);
    setAuthData(null);
  };

  if (isChecking) {
    return null; // or a loading spinner
  }

  if (!isAuthenticated) {
    addBreadcrumb("Household access denied - not authenticated", "auth", { 
      route: window.location.pathname 
    });
    
    logger.warn("Household access denied - user not authenticated", { 
      route: window.location.pathname 
    });
    
    return <Navigate to="/login" replace />;
  }

  // Check if children is a function (render prop pattern)
  if (typeof children === 'function') {
    return children({ onLogout: handleLogout, authData: authData });
  }
  
  // Otherwise, pass auth data and logout function to children as props
  return React.cloneElement(children, { 
    onLogout: handleLogout,
    authData: authData
  });
};

export default HouseholdProtectedRoute; 